import { Grid } from '@mui/material'
import Box from '@mui/material/Box'
import * as turf from '@turf/turf'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import AppContext from '../../context/appContext'
import { useExclusionAreas } from '../../hooks/data/useExclusionAreas'
import { useFarmSubdivisionsList } from '../../hooks/data/useFarmSubdivisionsList'
import { useMonitoringSites } from '../../hooks/data/useMonitoringSites'
import { GeoJSON } from '../../utils/GeoJSON/index'
import { isUnsavedSite } from '../../utils/samplePlanning/isUnsavedSite'
import { mapSavedSiteToFeature } from '../../utils/samplePlanning/mapSavedSiteToFeature'
import SamplePlanningMap from './SamplePlanningMap'
import SamplePlanningPanel from './SamplePlanningPanel'
import { ELIGIBILITY_QUERY, useEligibilityCheck } from '../../hooks/data/useEligibilityCheck'
import { useProjectArea } from '../../hooks/data/useProjectArea'
import { useEffectiveAreas } from '../../hooks/data/useEffectiveAreas'
import { useCroppedSamplingAreas } from '../../hooks/data/useSamplingAreas'

const serializedGeometry = feature => JSON.stringify(feature?.geometry ?? null)

// A VALUE identity, not an array reference: the strata query result changes identity on every
// refetch, and re-seeding on identity would discard the unsaved manual and random sites. Geometry
// is part of it because saving an exclusion area recrops the strata server-side under the same ids.
const strataSeedSignature = (farmId, croppedAreas) =>
  `${farmId}|${croppedAreas
    .map(
      area =>
        `${area.id}:${area.name}:${serializedGeometry(area.toGeoJSON)}:${serializedGeometry(area.toUncroppedGeoJSON)}`,
    )
    .join(',')}`

// Whole rows, so a server-side change to any site field counts and an equal refetch does not.
const sitesSeedSignature = sites => JSON.stringify(sites)

const SamplePlanningHome = () => {
  const [farm, setFarm] = useState()
  const [perimeter, setPerimeter] = useState()
  const [wasSaved, setWasSaved] = useState(false)
  const { currentFarm, programConfig } = useContext(AppContext)
  const [stratas, setStratas] = useState()
  const [monitoringSitesBySamplingAreas, setMonitoringSitesBySamplingAreas] = useState([])
  const { data: farmSubdivisions } = useFarmSubdivisionsList(currentFarm.id)
  const [mapClickCoordinates, setMapClickCoordinates] = useState(null)
  const [uncroppedStratas, setUncroppedStratas] = useState()
  const seededSignatureRef = useRef(null)
  const { eligibilityCheckAreas } = useEligibilityCheck({
    farmId: currentFarm.id,
    query: ELIGIBILITY_QUERY,
  })
  const { projectAreaGeometry } = useProjectArea({
    farmId: currentFarm.id,
    programConfig,
  })
  const { data: effectiveAreasData } = useEffectiveAreas({
    farmId: currentFarm.id,
  })
  const { data: croppedAreas } = useCroppedSamplingAreas({ farmId: currentFarm.id })
  const { data: sites } = useMonitoringSites({ farmId: currentFarm.id })
  const { data: exclusionAreas } = useExclusionAreas({ farmId: currentFarm.id })

  const eligibilityLayers = useMemo(
    () =>
      eligibilityCheckAreas?.map(area => ({
        name: area.properties.label,
        features: [area],
      })) || [],
    [eligibilityCheckAreas],
  )

  const grazingExclusionAreas = useMemo(
    () =>
      exclusionAreas && exclusionAreas.length > 0
        ? [
            {
              name: 'Exclusión con pastoreo',
              features: exclusionAreas
                .filter(area => {
                  const feature = area.toGeoJSON || area
                  return feature.properties.hasGrazingManagement
                })
                .map(area => {
                  const feature = area.toGeoJSON || area
                  return { ...feature, properties: { ...feature.properties, color: 'green' } }
                }),
            },
          ]
        : [],
    [exclusionAreas],
  )

  const projectAreaLayer = useMemo(
    () =>
      projectAreaGeometry
        ? [
            {
              name: 'Lotes ganaderos con MH',
              features: [
                {
                  ...projectAreaGeometry,
                  properties: {
                    ...projectAreaGeometry.properties,
                    color: '#FF6600',
                  },
                },
              ],
            },
          ]
        : [],
    [projectAreaGeometry],
  )

  const createSamplingAreaForSitesWithoutSamplingAreasAssociated = (savedSites, sArea) => {
    let samplingAreaForSitesWithoutSamplingAreas = null
    const sAreaIds = sArea.map(sa => sa.id)
    const monitoringWithoutSA = savedSites
      .filter(site => site.samplingAreaId == null || !sAreaIds.includes(site.samplingAreaId))
      .map(siteWithoutSA => {
        const feature = turf.point([siteWithoutSA.location[1], siteWithoutSA.location[0]])
        feature.properties.name = siteWithoutSA.name
        feature.properties.id = siteWithoutSA.id
        feature.properties.isRandomSite = siteWithoutSA.isRandomSite
        return feature
      })

    if (monitoringWithoutSA.length) {
      // In order to display monitoring sites that don't belong to a farm,
      // we need to create a custom strata feature and associate them to it
      samplingAreaForSitesWithoutSamplingAreas = turf.polygon([])
      samplingAreaForSitesWithoutSamplingAreas.properties.name = 'Sin estrato asociado'
      samplingAreaForSitesWithoutSamplingAreas.properties.color = 'black'
      samplingAreaForSitesWithoutSamplingAreas.properties.belongToFarm = false
    }

    return [samplingAreaForSitesWithoutSamplingAreas, monitoringWithoutSA]
  }
  const seedFromServer = useCallback(() => {
    const fC = turf.featureCollection([])
    const uncroppedFC = turf.featureCollection([])

    croppedAreas.forEach(sA => {
      const feature = sA.toGeoJSON
      const uncroppedFeature = sA.toUncroppedGeoJSON
      const commonProperties = {
        color: sA.color ? sA.color : `#${Math.floor(Math.random() * 16777215).toString(16)}`,
        area: GeoJSON.hectareArea(feature).toFixed(2),
        belongToFarm: true,
        name: sA.name,
        id: sA.id,
      }
      feature.properties = { ...feature.properties, ...commonProperties }
      uncroppedFeature.properties = { ...uncroppedFeature.properties, ...commonProperties }
      fC.features.push(feature)
      uncroppedFC.features.push(uncroppedFeature)
    })

    setFarm(currentFarm)
    setPerimeter(currentFarm.toGeoJSON)

    if (sites.length) {
      const newMonitoringSitesBySamplingAreas = {}

      // Esta sección contempla los sitios que poseen estrato asociado
      croppedAreas.forEach(area => {
        newMonitoringSitesBySamplingAreas[area.name] = sites.map(site => {
          if (site.samplingAreaId === area.id) {
            return mapSavedSiteToFeature(site, area.name)
          }
          return undefined
        })

        newMonitoringSitesBySamplingAreas[area.name] = newMonitoringSitesBySamplingAreas[
          area.name
        ].filter(site => {
          return site !== undefined
        })
      })

      // Esta sección contempla los sitios que no poseen estrato asociado
      const [samplingAreaForSitesWithoutSamplingAreas, monitoringWithoutSA] =
        createSamplingAreaForSitesWithoutSamplingAreasAssociated(sites, croppedAreas)
      if (samplingAreaForSitesWithoutSamplingAreas) {
        fC.features.push(samplingAreaForSitesWithoutSamplingAreas)
        newMonitoringSitesBySamplingAreas[
          samplingAreaForSitesWithoutSamplingAreas.properties.name
        ] = monitoringWithoutSA
      }

      setMonitoringSitesBySamplingAreas(newMonitoringSitesBySamplingAreas)
      if (sites.filter(site => site.isRandomSite).length > 0) {
        setWasSaved(true)
      }
    }

    setStratas(fC)
    setUncroppedStratas(uncroppedFC)
  }, [croppedAreas, sites, currentFarm])

  const handleMapClick = useCallback(
    (lat, lng) => {
      setMapClickCoordinates([lat, lng])
    },
    [setMapClickCoordinates],
  )

  const strataSignature = useMemo(
    () => croppedAreas && strataSeedSignature(currentFarm.id, croppedAreas),
    [croppedAreas, currentFarm.id],
  )
  const sitesSignature = useMemo(() => sites && sitesSeedSignature(sites), [sites])

  useEffect(() => {
    if (!strataSignature || !sitesSignature) return

    const seeded = seededSignatureRef.current
    const sameStrata = seeded?.strata === strataSignature
    if (sameStrata && seeded.sites === sitesSignature) return
    // A sites-only change waits while the panel holds sites the server lacks (a partly failed save
    // keeps its failures for the retry): the re-seed rebuilds the panel from the server alone.
    if (sameStrata && Object.values(monitoringSitesBySamplingAreas).flat().some(isUnsavedSite))
      return

    seededSignatureRef.current = { strata: strataSignature, sites: sitesSignature }
    seedFromServer()
  }, [strataSignature, sitesSignature, monitoringSitesBySamplingAreas, seedFromServer])

  return (
    <Box>
      {currentFarm && exclusionAreas && stratas && (
        <Grid container>
          <Grid item xs={6}>
            <SamplePlanningPanel
              effectiveAreasData={effectiveAreasData}
              eligibilityAreas={eligibilityCheckAreas}
              exclusionAreas={exclusionAreas}
              farm={farm}
              mapClickCoordinates={mapClickCoordinates}
              monitoringSitesBySamplingAreas={monitoringSitesBySamplingAreas}
              projectAreaGeometry={projectAreaGeometry}
              randomizeVersion={programConfig?.randomizeVersion || null}
              setMonitoringSitesBySamplingAreas={setMonitoringSitesBySamplingAreas}
              setWasSaved={setWasSaved}
              stratas={stratas}
              uncroppedStratas={uncroppedStratas}
              wasSaved={wasSaved}
            />
          </Grid>
          <Grid item xs={6}>
            <SamplePlanningMap
              layers={[
                ...farmSubdivisions,
                ...eligibilityLayers,
                ...grazingExclusionAreas,
                ...projectAreaLayer,
              ]}
              monitoringSitesBySamplingAreas={monitoringSitesBySamplingAreas}
              perimeter={perimeter}
              stratas={stratas}
              onMapClick={handleMapClick}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  )
}

export default SamplePlanningHome
